package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/retain")
@Composable
fun RetainPage() {
    BlogLayout(
        PageProperties(
            "retain",
            "retain persists values across configuration changes without requiring serialization, but does not survive process death."
        ),
        BlogProperties("Jetpack Compose", "retain"))
    {
        //survives recomposition and configuration changes, just like a ViewModel. No Parcelable needed. No Bundle limits.
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            //https://www.linkedin.com/in/deepakjangra91313/
            //RetainedEffect
            SpanText(
                text = "retain - (Persist Objects Across Configuration Changes (But Not Process Death))",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "retain persists values across configuration changes without requiring serialization, but does not survive process death.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "survives recomposition, rotation, navigation, leaving composition, and lives *inside* Compose. No ViewModel needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberRetained() is a composable function that preserves state across configuration changes while remaining lightweight and composition-aware.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This API uses RetainScope to manage when values should be kept in memory during screen transitions, configuration changes, or navigation events. The retain API provides a compose-native alternative to ViewModels for localized state persistence, with lifecycle callbacks through RetainObserver interface and automatic cleanup when values are no longer needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Keep long-lived, non-serializable objects alive across rotation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Such as:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ExoPlayer",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Camera objects",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Bitmaps",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Flows and Channels",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lambdas",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Navigation back stack preservation",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Expensive caches",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Analytics trackers",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Network clients (if scoped intentionally)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "WebView controllers",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    dependencies {
                        implementation("androidx.compose.runtime:runtime:")
                        implementation("androidx.compose.runtime:runtime-saveable:")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    data class ComplexUser(
                        val profile: UserProfile,
                        val settings: Settings,
                        val preferences: Map<String, Any>
                    )

                    // ❌ Won't work - ComplexUser isn't Parcelable
                    var user by rememberSaveable { mutableStateOf(ComplexUser()) }
                    
                    // Large data can exceed Bundle size limit (1MB)
                    var largeDataset by rememberSaveable { 
                        mutableStateOf(List(10000) { "Item $it" }) 
                    } // ❌ Might crash
                    
                    data class ComplexUser(
                        val profile: UserProfile,
                        val settings: Settings,
                        val preferences: Map<String, Any>
                    )

                    // ❌ Won't work - ComplexUser isn't Parcelable
                    var user by rememberSaveable { mutableStateOf(ComplexUser()) }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The issue: rememberSaveable requires data to be Parcelable or primitive types. Complex objects, third-party classes, or objects with non-serializable fields won't work.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Simple ViewModel usage without factories
                    val viewModel = retain { MyViewModel() }
                    
                    // Large lists that would be expensive to serialize
                    val items = retain { mutableStateListOf<Item>() }
                    
                    // ❌ Wrong tool - use Room, DataStore, or SharedPreferences
                    val userSettings = retain { UserSettings() }
                    
                    // ❌ Overkill - use remember
                    val expanded by retain { mutableStateOf(false) }
                    // ✅ Better
                    val expanded by remember { mutableStateOf(false) }
                    
                    // ❌ Overkill - use rememberSaveable
                    val count by retain { mutableStateOf(0) }
                    // ✅ Better
                    val count by rememberSaveable { mutableStateOf(0) }
                    
                    // ❌ Bad - Context will be stale after configuration change
                    val context = retain { LocalContext.current }
                    // ✅ Good - Get fresh context each time
                    val context = LocalContext.current
                    
                    //Key base 
                    val userData = remember(userId) {
                        retain {
                            mutableStateOf<User?>(null).apply {
                            loadUserData(userId)
                            }   
                        }
                    }
                    
                    //
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // ❌ BAD - Activity context could leak
                    val mediaPlayer = retain { ExoPlayer.Builder(context).build() }
                    
                    // ✅ GOOD - Application context is safe
                    val appContext = LocalContext.current.applicationContext
                    val player = retain { ExoPlayer.Builder(appContext).build() }
                    
                    // Use mediaPlayer within your composable
                    DisposableEffect(mediaPlayer) {
                        onDispose {
                            mediaPlayer.release()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Lazy Initialization
                    
                    @Composable
                    fun HeavyResourceScreen() {
                        val heavyResource = remember {
                            retain {
                                lazy {
                                    HeavyResource.initialize() 
                                }
                            }
                        }
                        
                        val data = remember(heavyResource) {
                            derivedStateOf {
                                heavyResource.value.processData()
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Real-World Example: Media Player
                    
                    @Composable
                    fun VideoPlayer(videoUrl: String) {
                        val context = LocalContext.current.applicationContext
                        
                        // Avoids player recreation during configuration changes
                        val exoPlayer = retain(videoUrl) {
                            ExoPlayer.Builder(context)
                                .build()
                                .apply {
                                    setMediaItem(MediaItem.fromUri(videoUrl))
                                    prepare()
                                }
                        }
                        
                        // Handle lifecycle events for proper pause/resume
                        val lifecycleOwner = LocalLifecycleOwner.current
                        DisposableEffect(lifecycleOwner) {
                            val observer = LifecycleEventObserver { _, event ->
                                when (event) {
                                    Lifecycle.Event.ON_PAUSE -> exoPlayer.pause()
                                    Lifecycle.Event.ON_RESUME -> exoPlayer.play()
                                    else -> {}
                                }
                            }
                            lifecycleOwner.lifecycle.addObserver(observer)
                            
                            onDispose {
                                // Only remove the observer here - NOT the player release
                                lifecycleOwner.lifecycle.removeObserver(observer)
                            }
                        }
                        
                        // Release player only on permanent removal, not config changes
                        RetainedEffect(videoUrl) {
                            onRetire {
                                exoPlayer.release()
                            }
                        }
                        
                        AndroidView(
                            factory = { ctx ->
                                PlayerView(ctx).apply {
                                    player = exoPlayer
                                }
                            },
                            modifier = Modifier.fillMaxSize()
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Create Remember Factory Functions
                    
                    @Composable
                    fun rememberImageState(
                        imageUri: String,
                        initialZoom: Float = 1f
                    ): ImageState {
                        return retain(imageUri) {
                            ImageState(imageUri, initialZoom)
                        }
                    }
                    
                    // Usage
                    @Composable
                    fun ImageViewer(uri: String) {
                        val state = rememberImageState(uri)
                        // ...
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun MediaPlayerScreen() {
                        val context = LocalContext.current.applicationContext

                        val exoPlayer = retain {
                            ExoPlayer.Builder(context)
                                .build()
                                .apply {
                                    setMediaItem(MediaItem.fromUri("https://sample.com/video.mp4"))
                                    prepare()
                                    playWhenReady = true
                                }
                        }

                        AndroidView(
                            factory = {
                                PlayerView(it).apply { player = exoPlayer }
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                //Complex Object

                code = """
                    class NonSerializableRepository(
                        private val apiService: ApiService,
                        private val database: AppDatabase
                    ) {
                        suspend fun fetchData(): List<User> {
                           
                        }
                    }

                    @Composable
                    fun rememberRepository(): NonSerializableRepository {
                        return remember {
                            retain {
                                NonSerializableRepository(
                                    apiService = ApiService.create(),
                                    database = AppDatabase.getInstance()
                                )
                            }
                        }
                    }

                    @Composable
                    fun UserListScreen() {
                        val repository = rememberRepository()
                        var users by remember { mutableStateOf(emptyList<User>()) }
                        
                        LaunchedEffect(Unit) {
                            users = repository.fetchData()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Custom Retain
                    
                    class DatabaseRetainer<T : Any>(
                        private val database: AppDatabase,
                        private val key: String
                    ) : Retainer<T> {
                        
                        override fun save(value: T) {
                            database.saveRetainedValue(key, value)
                        }
                        
                        override fun restore(): T? {
                            return database.getRetainedValue<T>(key)
                        }
                    }

                    @Composable
                    fun <T : Any> rememberRetainedWithDatabase(
                        key: String,
                        defaultValue: () -> T
                    ): T {
                        val database = LocalDatabase.current
                        
                        return remember(key) {
                            retain(
                                retainer = DatabaseRetainer(database, key),
                                factory = defaultValue
                            )
                        }
                    }

                    @Composable
                    fun ComplexSettingsScreen() {
                        val userPreferences = rememberRetainedWithDatabase(
                            key = "user_preferences"
                        ) {
                            UserPreferences(
                                theme = Theme.SYSTEM,
                                notificationsEnabled = true,
                                language = "tr"
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Lifecycle States",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Active → in composition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Retained → temporarily removed but alive",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Retired → discarded forever",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://medium.com/@anandyadav.it10/jetpack-compose-retain-api-complete-guide-to-state-persistence-in-android-5c5ce9b24137
            SpanText(
                text = "Lifecycle of Retained Objects",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Retained → The object is first captured as the return result of the calculation block in retain.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Entered Composition → The composition hierarchy successfully realizes this value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Exited Composition → The composition no longer references the object, but it may be restored.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Retired → The object is permanently released and will not be reused.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "𝗛𝗼𝘄 `𝗿𝗲𝘁𝗮𝗶𝗻{}` 𝘄𝗼𝗿𝗸𝘀 (𝘀𝗶𝗺𝗽𝗹𝗲)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Remember phase → retain creates a value holder like remember",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Retention phase → when UI disappears, the value is NOT destroyed If the same composable returns with the same keys → the old value is restored automatically. Feels like ViewModel behavior, but scoped to the composition itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "𝗪𝗵𝗲𝗿𝗲 `𝗿𝗲𝘁𝗮𝗶𝗻{}` 𝗵𝗲𝗹𝗽𝘀",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //Transient navigation state
            //Form data preservation
            //Media playback state
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text =  "✔ Multi-step forms\n" +
                                "✔ Bottom sheets and tab UIs\n" +
                                "✔ Scroll position preservation\n" +
                                "✔ Avoiding repeated API calls\n" +
                                "✔ Cases where ViewModel feels too heavy\n" +
                                "✔ Survives recomposition\n" +
                                "✔ Survives configuration change\n" +
                                "❌ Does not survive process death\n" +
                                "✔ Does not serialize your object\n" +
                                "✔ Releases object when the underlying Activity is destroyed\n" +
                                "✔ Retaining expensive objects without serialization",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When to prefer ViewModel instead",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Complex business logic requiring dependency injection",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data that needs to survive process death",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State shared across multiple screens",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Repository pattern implementations",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "RetainScope",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Behind the scenes, retain is managed by an internal RetainScope, which defines a boundary for temporarily removed content. Developers do not directly control or create RetainScope—it's handled implicitly by the Compose runtime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The lifecycle of a retained value follows these steps:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Retention begins: The scope is notified that content is about to be temporarily removed (e.g., during configuration change)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Content exits composition: Values from remember are forgotten, but retain values are handed to the scope",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Content re-enters: When the composable returns with matching keys, the retained value is restored",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Retention ends: Unused retained values are “retired” (cleaned up)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            WebImage(
                src = Resources.Images.SIDE_EFFECT_RETAIN_SCOPE,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Retain Scope"
            )

            SpanText(
                text = "RetainObserver Interface",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For advanced use cases where you need lifecycle awareness of your retained objects, Compose provides the RetainObserver interface. This interface allows objects to be notified of their retention status and respond accordingly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    interface RetainObserver {
                        fun onRetained()
                        fun onEnteredComposition()
                        fun onExitedComposition()
                        fun onRetired()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class RetainedImageLoader : RetainObserver {
                        private val imageCache = mutableMapOf<String, Bitmap>()
                        private var cacheJob: Job? = null
                        private val scope = CoroutineScope(Dispatchers.Default)
                        
                        val images: StateFlow<List<Bitmap>> = 
                            MutableStateFlow(emptyList())
                        
                        override fun onRetained() {
                            // Object is first created and retained
                            println("ImageLoader retained - initializing")
                            startCachePreloading()
                        }
                        
                        override fun onEnteredComposition() {
                            // Composition is actively using this object
                            println("ImageLoader entered composition - resuming updates")
                            resumeImageUpdates()
                        }
                        
                        override fun onExitedComposition() {
                            // Temporarily not in composition but retained
                            println("ImageLoader exited composition - pausing updates")
                            pauseImageUpdates()
                        }
                        
                        override fun onRetired() {
                            // Permanently released
                            println("ImageLoader retired - cleaning up")
                            cacheJob?.cancel()
                            imageCache.clear()
                            scope.cancel()
                        }
                        
                        private fun startCachePreloading() {
                            cacheJob = scope.launch {
                                // Preload images that persist across config changes
                            }
                        }
                        
                        private fun resumeImageUpdates() {
                            // Resume active image loading for UI
                        }
                        
                        private fun pauseImageUpdates() {
                            // Pause but keep cached data
                        }
                    }

                    @Composable
                    fun ImageGalleryScreen() {
                        val imageLoader = rememberRetained { RetainedImageLoader() }
                        
                        val images by imageLoader.images.collectAsState()
                        
                        LazyColumn {
                            items(images) { bitmap ->
                                Image(bitmap = bitmap.asImageBitmap(), contentDescription = null)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "RetainedEffect",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose also provides RetainedEffect for running side effects tied to the retention lifecycle:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun AnalyticsTracker(screenId: String) {
                        val tracker = retain(screenId) { createTracker(screenId) }
                        
                        RetainedEffect(screenId) {
                            // Runs when entering composition
                            tracker.trackScreenView()
                            
                            onRetire {
                                // Cleanup when retired (not on config change)
                                tracker.dispose()
                            }
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect, which runs disposal when leaving composition, RetainedEffect only disposes when truly retired.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Use retain when",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State is local to a specific composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Heavy objects like repositories, use cases, or coroutine scopes",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Avoiding ViewModel boilerplate for smaller, scoped composables",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Large objects like bitmaps, flows, lambdas that can't be serialized",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Objects that must outlive configuration changes",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You need to persist non-serializable objects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to avoid ViewModel boilerplate for simple cases.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Building navigation flows where screens need independent state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Use viewModel when",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State needs to be shared across screens.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You need SavedStateHandle for process death survival.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Business logic should be separated from UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You need Hilt, Koin dependency injection.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember Vs retain",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember only survives recompositions, retain allows values to persist even when the associated composable leaves the composition entirely. For instance, if a screen is navigated away from but remains in the back stack, or if an Activity is recreated during a rotation, any value remembered with remember will be discarded. In contrast, a value created with retain can remain in memory and be brought back when the composable returns to the composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "retain vs ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                //retain
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Scoped to composable's position in composition tree",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "No sharing between siblings",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When permanently leaving composition",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose-native lifecycle",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Local Ul state, caches",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                //ViewModel
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Singleton in ViewModelStore",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Shared across composables",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When ViewModelStore cleared",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Coroutine scope, SavedStateHandle, Hilt",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Business logic, shared state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }
        }
    }
}

//https://github.com/philipplackner/AndroidStateManagement-CustomStateHolders.git